import type { IReactionDisposer } from 'mobx';
import { reaction } from 'mobx';
import type { IViewItemsViewModelController, ISelectableItem } from './interfaces';
import type { ViewsService } from './Views.service';
import type { IDisposable } from '@/engine/Utilities/IDisposable';
import { iocContainer, TYPES } from '@/engine/ioc';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import { AutoDisposer } from '@/engine/Utilities/AutoDisposer';
import type { ImagesService } from './modules/images/Images.service';
import { ViewModelSelectionReactionStrategiesProvider } from './ViewModelSelectionReactionStrategies';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { ViewController } from './View.controller';

export interface ISelectionReactionStrategy {
  reactOnSelection: (viewModel: ISelectableItem) => void;
}

export class SelectionReactionController implements IDisposable {
  private readonly _viewsService: ViewsService;

  private _selectableItems?: Array<ISelectableItem>;

  private _subscriptions: DisposablesBasket = new DisposablesBasket();

  private _selectionSubscriptions: DisposablesBasket = new DisposablesBasket();

  private _changeReactionDisposer: IReactionDisposer;

  private _viewModelSelectionReactionStrategiesProvider: ViewModelSelectionReactionStrategiesProvider;

  public constructor(
    viewsService: ViewsService,
    viewController: ViewController,
    imagesService: ImagesService,
    planningAlgorithm: PlanningAlgorithm
  ) {
    this._viewsService = viewsService;
    const controller = iocContainer.get<IViewItemsViewModelController>(TYPES.IViewItemsViewModelController);

    this._selectableItems = this._getSelectableItems();

    this._viewModelSelectionReactionStrategiesProvider = new ViewModelSelectionReactionStrategiesProvider(
      controller,
      this._viewsService,
      viewController,
      imagesService,
      planningAlgorithm
    );

    this._subscribeForSelection();

    this._changeReactionDisposer = reaction(
      () => this._getSelectableItems(),
      (viewModels) => {
        this._selectableItems = viewModels;
        this._selectionSubscriptions.dispose();
        this._subscribeForSelection();
      }
    );

    this._subscriptions.add(imagesService.subscribeToReadyStateChange(this._handleImagesReadyStateChange.bind(this)));
  }

  private _getSelectableItems(): Array<ISelectableItem> | undefined {
    return this._viewsService.currentView?.getSelectableItems();
  }

  private _subscribeForSelection(): void {
    if (!this._selectableItems) {
      return;
    }
    this._selectableItems.forEach((viewItem: ISelectableItem) => {
      this._selectionSubscriptions.add(
        new AutoDisposer(
          reaction(
            () => viewItem.isSelected,
            (selection) => {
              if (selection) {
                const selectionReactionStrategy = this._viewModelSelectionReactionStrategiesProvider.getStrategy(
                  viewItem.kind
                );
                selectionReactionStrategy?.reactOnSelection(viewItem);
              }
            }
          )
        )
      );
    });
  }

  private _handleImagesReadyStateChange(areImagesReady: boolean): void {
    if (areImagesReady && this._selectableItems) {
      const selected = this._selectableItems.find((ms) => ms.isSelected);
      if (selected) {
        const selectionReactionStrategy = this._viewModelSelectionReactionStrategiesProvider.getStrategy(selected.kind);
        selectionReactionStrategy?.reactOnSelection(selected);
      }
    }
  }

  public dispose(): void {
    this._subscriptions.dispose();
    this._selectionSubscriptions.dispose();
    this._changeReactionDisposer();
  }
}
